import React, { useEffect, useMemo, useRef, useState } from "react";

// Pirámide de cuadrados iguales: jugadores abajo (16 → 8 → 4 → 2 → 1 hasta la
// Final), unidos solo por líneas. Entre rondas solo queda el espacio justo para
// las líneas y el nombre de la ronda, así el cuadro cabe entero en la pantalla y
// los cuadrados se ven lo más grandes posible.
const SQUARE = 130;
const GAP = 6;
const ROW_GAP = 46;
const TITLE_SPACE = 34;

const ROUND_COLORS = {
  roundOf16: "#b84dff",
  quarterfinal: "#2f8bff",
  semifinal: "#ff3b3b",
  final: "#f2c230",
};
// Verde = partido jugado o en juego: la pirámide se va llenando de verde hasta la Final.
const PROGRESS_COLOR = "#39ff14";

const ChampionshipBracket = ({ rounds, seats, roundTitles, renderMatch, renderSeat }) => {
  const wrapperRef = useRef(null);
  const [available, setAvailable] = useState({ width: 0, height: 0 });

  // Cabe a lo ancho del panel y a lo alto de la ventana (sin desplazarse).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver !== "function") return undefined;
    const measure = () => {
      let scroller = wrapper.parentElement;
      while (scroller && scroller !== document.body && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
        scroller = scroller.parentElement;
      }
      const scrollTop = scroller && scroller !== document.body ? scroller.scrollTop : window.scrollY;
      const topInPage = wrapper.getBoundingClientRect().top + scrollTop;
      setAvailable({
        width: wrapper.clientWidth,
        height: Math.max(300, window.innerHeight - topInPage - 24),
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const layout = useMemo(() => {
    const seatCount = Math.max(2, seats.length);
    const levels = rounds.length;
    const pitch = SQUARE + GAP;
    const width = seatCount * SQUARE + (seatCount - 1) * GAP;
    const step = SQUARE + ROW_GAP;
    const height = TITLE_SPACE + levels * step + SQUARE;
    const centerYOfLevel = (level) => height - SQUARE / 2 - level * step;

    const seatNodes = seats.map((entry, index) => ({
      entry,
      x: SQUARE / 2 + index * pitch,
      y: centerYOfLevel(0),
    }));
    let previous = seatNodes;
    const roundNodes = rounds.map((round, roundIndex) => {
      const nodes = round.matches.map((match, index) => {
        const left = previous[index * 2];
        const right = previous[index * 2 + 1];
        const x = left && right ? (left.x + right.x) / 2 : left?.x || 0;
        return { match, x, y: centerYOfLevel(roundIndex + 1), children: [left, right].filter(Boolean) };
      });
      previous = nodes;
      return { key: round.key, nodes };
    });
    return { width, height, seatNodes, roundNodes };
  }, [rounds, seats]);

  const scale =
    available.width > 0
      ? Math.min(1, available.width / layout.width, available.height / layout.height)
      : 1;

  return (
    <div className="xc-pyramid-wrap" ref={wrapperRef}>
      <div className="xc-pyramid-scroll" style={{ height: layout.height * scale }}>
        <div style={{ width: layout.width * scale, margin: "0 auto", position: "relative" }}>
        <div
          className="xc-pyramid"
          style={{ width: layout.width, height: layout.height, transform: `scale(${scale})` }}
        >
          <svg className="xc-pyramid-lines" width={layout.width} height={layout.height} aria-hidden="true">
            {layout.roundNodes.map(({ key, nodes }) =>
              nodes.map((node) => {
                const played = Boolean(node.match.winner) || Boolean(node.match.live);
                const color = played ? PROGRESS_COLOR : ROUND_COLORS[key] || PROGRESS_COLOR;
                const parentBottom = node.y + SQUARE / 2;
                const midY = parentBottom + (node.children[0].y - SQUARE / 2 - parentBottom) / 2;
                return (
                  <g key={node.match.id} stroke={color} style={{ color }}>
                    <line x1={node.x} y1={parentBottom} x2={node.x} y2={midY} />
                    {node.children.map((child, index) => (
                      <polyline
                        key={index}
                        fill="none"
                        points={`${node.x},${midY} ${child.x},${midY} ${child.x},${child.y - SQUARE / 2}`}
                      />
                    ))}
                  </g>
                );
              }),
            )}
          </svg>

          {layout.roundNodes.map(({ key, nodes }) => (
            <div
              key={`title-${key}`}
              className={`xc-pyramid-title xc-round-${key}`}
              style={{ top: nodes[0].y - SQUARE / 2 - 22, left: 0, width: layout.width }}
            >
              {roundTitles[key] || key}
            </div>
          ))}

          {layout.roundNodes.map(({ key, nodes }) =>
            nodes.map((node) => (
              <div
                key={node.match.id}
                className={`xc-square xc-round-${key}${node.match.winner || node.match.live ? " is-progress" : ""}`}
                style={{ left: node.x - SQUARE / 2, top: node.y - SQUARE / 2, width: SQUARE, height: SQUARE }}
              >
                {renderMatch(node.match, key)}
              </div>
            )),
          )}

          {layout.seatNodes.map((node, index) => (
            <div
              key={`seat-${index}`}
              className="xc-square xc-seat-square"
              style={{ left: node.x - SQUARE / 2, top: node.y - SQUARE / 2, width: SQUARE, height: SQUARE }}
            >
              {renderSeat(node.entry, index)}
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  );
};

export default ChampionshipBracket;
